import React, { useEffect } from "react";

import {
  AutoComplete,
  Badge,
  Box,
  Cell,
  CustomModalLayout,
  Divider,
  Dropdown,
  Heading,
  Layout,
  Modal,
  Pagination,
  Table,
  Text,
  Tooltip,
} from "@wix/design-system";

import picture from "../../../dashboard/images/no-image.png";
import FiareLogo from "../../../dashboard/pages/fiare_logo.svg";
import WixLogo from "../../../dashboard/pages/wix_logo.svg";

import { removeDuplicates, truncateText } from "../../../services/utils/productUtils";
import WixProduct from "../../../models/wix-product";
import Loading from "../Loader";
import FaireProduct from "../../../models/faire-product";

const ExportModal = (props: any) => {
  // get Viewport width
  let viewportWidth = window.innerWidth || document.documentElement.clientWidth;

  // Calculate 60% of the viewport widthz
  let boxWidth = Math.floor(0.6 * viewportWidth);

  /*
  |--------------------------------------------------------------------------
  | States
  |--------------------------------------------------------------------------
  |
  */
  const [products, setProducts] = React.useState([]);
  const [totalPage, setTotalPage] = React.useState(1);
  const [currentPage, setCurrentPage] = React.useState();
  const [isLoading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  /*
  |--------------------------------------------------------------------------
  | Handle Page Change
  |--------------------------------------------------------------------------
  |
  */
  const pageChangeHandler = (event: any) => {
    setCurrentPage(event.page);
  };

  /*
  |--------------------------------------------------------------------------
  | Handle Selected Products
  |--------------------------------------------------------------------------
  |
  */
  const handleSelectedProducts = () => {
    let selectedProducts = removeDuplicates(props.selectedProducts);

    const parameters = {
      perPage: 4,
      page: currentPage,
    };

    let params = {};

    if (props.source === "wix") {
      params = {
        withVariants: 1,
        withCollections: 1,
        productIds: selectedProducts.toString(),
      };
    } else {
      params = {
        productIds: selectedProducts.toString(),
      };
    }

    const allParameters = {
      ...parameters,
      ...params,
    };

    setLoading(true);

    if (props.source === "wix") {
      WixProduct.get(allParameters)
        .then((response) => {
          setProducts(response.data.data);

          const total = response.data.last_page;
          setTotalPage(total);
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setLoading(false);
        });
    } else {
      FaireProduct.get(allParameters)
        .then((response) => {
          setProducts(response.data.data);

          const total = response.data.last_page;
          setTotalPage(total);
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setLoading(false);
        });
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Check If Category Selected
  |--------------------------------------------------------------------------
  |
  */
  const checkCategorySelected = (event: any) => {
    event.preventDefault();
    if (props.source === "wix") {
      if (props.categoryId !== "0") {
        props.handleExport(props.categoryId);
        return setError(false);
      }
      return setError(true);
    } else {
      return props.handleExport();
    }
  };

  useEffect(() => {
    if (props.showExportConfirmation === true) {
      handleSelectedProducts();
    }
  }, [props.showExportConfirmation, currentPage]);

  /*
  |--------------------------------------------------------------------------
  | Table Columns
  |--------------------------------------------------------------------------
  |
  */
  const columns: any = [
    {
      title: "Thumbnail",
      width: "5%",
      render: (row: any) => (
        <Layout>
          <Cell span={3}>
            <Box width="40px" height="45px" overflow="hidden">
              <img src={row.thumbnail ? row.thumbnail : picture} />
            </Box>
          </Cell>
        </Layout>
      ),
    },
    {
      title: "Category",
      width: "33%",
      render: (row: any) => (
        <Box direction="vertical">
          <Text size="medium" weight="bold">
            {truncateText(row.name, 48)}
          </Text>
          <Text size="tiny">
            Category:
            {props.source === "faire"
              ? row.taxonomies.length <= 1
                ? row.taxonomies[0]?.name
                : row.taxonomies[0]?.name + `and ${row.taxonomies.length} more`
              : row.collections.map((item: any) => item.name).join(", ")}
          </Text>
        </Box>
      ),
    },

    {
      title: "Status",
      align: "center",
      render: (row: any) => (
        <Badge
          size="small"
          skin={
            props.source === "wix"
              ? row.visible
                ? "success"
                : "neutralLight"
              : row.lifecycle_state === "DRAFT"
              ? "standard"
              : row.lifecycle_state === "PUBLISHED"
              ? "success"
              : row.lifecycle_state === "DELETED"
              ? "danger"
              : row.lifecycle_state === "UNPUBLISHED"
              ? "neutral"
              : "general"
          }
        >
          {props.source === "wix" ? (row.visible ? "Visible" : "Invisible") : row.lifecycle_state}
        </Badge>
      ),
    },
    {
      title: "Inventory",
      width: "15%",
      render: (row: any) => (
        <Text>
          {props.source === "faire"
            ? `${row.total_quantity ?? 0} in stock` +
              (row.numberOfVariants > 0
                ? ` for ${row.numberOfVariants} variant${row.numberOfVariants > 1 ? "s" : ""}`
                : "")
            : row.trackInventory
            ? `${row.quantity} in stock` +
              (row.numberOfVariants > 0
                ? ` for ${row.numberOfVariants} variant${row.numberOfVariants > 1 ? "s" : ""}`
                : "")
            : row.inStock
            ? "In Stock"
            : "Out of stock"}
        </Text>
      ),
    },
    {
      title: "View on",
      align: "center",
      render: (row: any) => (
        <Tooltip
          appendTo="window"
          content={`View the product on ${props.source === "wix" ? "Wix" : "Faire"} dashboard`}
        >
          <a href={row.details_url} target="_blank">
            {props.source === "wix" ? <img width={"35px"} src={WixLogo} /> : <img src={FiareLogo} />}
          </a>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      isOpen={props.showExportConfirmation}
      onRequestClose={() => props.setShowExportConfirmation(false)}
      shouldCloseOnOverlayClick
      screen="desktop"
      onAfterClose={props?.onAfterClose}
    >
      <CustomModalLayout
        removeContentPadding
        primaryButtonText={props.source === "faire" ? "Import" : "Export"}
        primaryButtonOnClick={(event: any) => {
          checkCategorySelected(event);
        }}
        onCloseButtonClick={() => {
          props.setShowExportConfirmation(false);
          props.setCategoryId("0");
        }}
        secondaryButtonText="Cancel"
        secondaryButtonOnClick={() => {
          props.setShowExportConfirmation(false);
          props.setCategoryId("0");
        }}
        title={
          <Box style={{ justifyContent: "space-between" }}>
            <Heading size="large">{props?.title}</Heading>

            {props.source === "wix" && (
              <Box direction="vertical">
                <AutoComplete
                  id="export_DropdownId"
                  size="small"
                  border="round"
                  focusOnSelectedOption
                  selectedId={props.categoryId}
                  onSelect={props.onSelect}
                  placeholder={props.placeholder}
                  infiniteScroll
                  hasMore={props.hasMore}
                  loadMore={props?.loadMore}
                  options={props?.options}
                  onChange={props?.onChange}
                  value={props?.value}
                />
                <Text skin="error" size="small">
                  {error && "*Category is required."}
                </Text>
              </Box>
            )}
          </Box>
        }
        subtitle={props?.subtitle}
        content={
          <Box align="center" maxHeight={"390px"} minHeight={"100px"} width={`${boxWidth}px`}>
            <Layout justifyItems="center">
              <Cell>
                <Table data={products} columns={columns} rowVerticalPadding="tiny">
                  {isLoading ? (
                    <Loading title="Fetching Selected Products" />
                  ) : (
                    <Table.Content titleBarVisible={false} />
                  )}
                </Table>
                <Divider />
              </Cell>

              <Cell>
                <Pagination currentPage={currentPage} onChange={pageChangeHandler} totalPages={totalPage} />
              </Cell>
            </Layout>
          </Box>
        }
      />
    </Modal>
  );
};

export default ExportModal;
